/* eslint-disable max-lines */
import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet";
import { useHistory } from "react-router-dom";
import { motion } from "framer-motion";
import { useStoreState, useStoreActions } from "easy-peasy";
import { TwoColumnLayout } from "../templates";
import { softwareTokenMfa } from "../api/software-token-mfa";
import { notifyError } from "../lib/notifications";
import { INTERCOM_2FA_HELP_ARTICLE } from "../constants";
import { settings, animations } from "../settings";
import { redirectAfterLogin } from "../lib/redirect-after-login";

export const VerifyTwoFactor = () => {
  const history = useHistory();
  const challenge = useStoreState((state) => state.challenge);
  const setIsAuthenticated = useStoreActions(
    (actions) => actions.setIsAuthenticated,
  );
  const setChallenge = useStoreActions((actions) => actions.setChallenge);

  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const { animate } = settings;
  const { form } = animations;

  useEffect(() => {
    if (!challenge) {
      history.push("/login");
      return;
    }
    if (challenge.name === "NEW_PASSWORD_REQUIRED") {
      history.push("/set-new-password");
      return;
    }
    if (challenge.name !== "SOFTWARE_TOKEN_MFA") {
      history.push("/login");
    }
  }, [challenge, history]);

  const codeNotProvided = () => code === null || code.length === 0;

  const backToLoginPage = () => {
    return history.push("/login");
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    setError("");

    if (codeNotProvided()) {
      setError("One time code missing");
      return notifyError("One time code missing");
    }

    if (!challenge || challenge.name !== "SOFTWARE_TOKEN_MFA") {
      return notifyError("Session expired. Please log in again.");
    }

    setIsLoading(true);
    const body = {
      code,
      session: challenge.session,
      username: challenge.username,
      password: challenge.password,
    };

    return softwareTokenMfa(body)
      .then((res) => {
        if (res.data?.challenge) {
          setChallenge(res.data.challenge);
          history.push("/set-new-password");
          return true;
        }
        setIsAuthenticated(true);
        return redirectAfterLogin(history);
      })
      .catch((err) => {
        setIsLoading(false);

        if (!err.response) {
          setError("Network error");
          return notifyError("Network error");
        }

        if (err.response.status === 400) {
          setError("Wrong or expired code");
          return notifyError("Wrong or expired code");
        }

        if (err.response.status === 401) {
          return backToLoginPage();
        }

        setError("Something went wrong");
        return notifyError("Something went wrong");
      });
  };

  const handleCodeChange = (e) => {
    setCode(e.target.value);
    setError("");
  };

  const leftContent = (
    <div className="absolute" style={{ inset: -2 }}>
      <picture>
        <source
          media="(min-width: 1024px)"
          srcSet="/design/images/login-page/2fa-desktop.webp"
        />
        <img
          src="/design/images/login-page/2fa-tablet.webp"
          alt="Two-factor authentication illustration"
          className="w-full h-full object-cover"
        />
      </picture>
      <div className="absolute z-10" style={{ top: '2.5rem', left: '2.5rem' }}>
        <img
          src="/design/images/login-page/breathe_logo_white.svg"
          alt="Breathe logo icon"
          className="h-8 w-9"
        />
      </div>
    </div>
  );

  if (!challenge || challenge.name !== "SOFTWARE_TOKEN_MFA") {
    return null; // useEffect handles navigation away
  }

  return (
    <>
      <Helmet>
        <title>Two-factor authentication</title>
      </Helmet>

      <TwoColumnLayout leftContent={leftContent}>
        <div className="flex flex-col h-full">
          <div className="flex-1 flex flex-col items-center justify-center">
            <motion.div
              variants={form}
              initial={animate ? "hidden" : ""}
              animate={animate ? "show" : ""}
              className="w-full max-w-[374px]"
            >
              <div className="flex flex-col gap-6 items-center justify-center w-full">
                <div className="flex flex-col items-start w-full">
                  <h1
                    className="text-[30px] font-bold leading-[36px] login-text-foreground mb-0 font-rubik"
                    style={{
                      fontWeight: 700,
                      fontSize: "30px",
                      lineHeight: "36px",
                    }}
                  >
                    We&apos;re checking if it&apos;s you
                  </h1>
                </div>

                <div className="flex flex-col gap-6 items-center justify-center w-full">
                  <form
                    onSubmit={handleSubmit}
                    className="flex flex-col gap-6 items-start w-full"
                  >
                    <div className="flex flex-col gap-3 items-start w-full">
                      <div className="flex flex-col gap-2 items-start w-full">
                        {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                        <label
                          htmlFor="auth-code"
                          className="text-sm font-normal leading-none login-text-foreground font-inter"
                        >
                          Enter your one-time code from your authenticator app
                        </label>
                        <input
                          id="auth-code"
                          className={`w-full login-input login-text-muted font-inter tracking-widest ${
                             error ? "border-red-500" : ""
                           }`}
                          autoFocus
                          placeholder="Enter code"
                          value={code}
                          onChange={handleCodeChange}
                          type="text"
                          disabled={isLoading}
                        />
                        {error && (
                        <p
                          className="text-sm text-red-500 font-inter"
                          role="alert"
                        >
                          {error}
                        </p>
                        )}
                      </div>

                      <button
                        type="submit"
                        disabled={isLoading || codeNotProvided()}
                        className="w-full login-button rounded-md login-bg-primary text-white text-sm font-normal flex items-center justify-center font-inter disabled:opacity-50 disabled:cursor-not-allowed"
                        style={{
                          height: "36px",
                          padding: "8px 16px",
                          backgroundColor: "#2563eb",
                          color: "#fafafa",
                        }}
                      >
                        {isLoading ? "Verifying..." : "Submit"}
                      </button>
                    </div>
                  </form>

                  <div className="flex items-center justify-center w-full">
                    <ul
                      className="list-disc text-sm login-text-foreground font-inter leading-5"
                      style={{ paddingLeft: "21px" }}
                    >
                      <li className="mb-0">
                        <span className="leading-[20px]">
                          Your organisation has switched on two-factor
                          authentication to keep your data safe.
                        </span>
                      </li>
                      <li className="mb-0">
                        <span className="leading-[20px]">
                          To access your account, open your authenticator app
                          and enter the one-time code that appears.
                        </span>
                      </li>
                      <li>
                        <span className="leading-[20px]">
                          <a
                            href={
                              INTERCOM_2FA_HELP_ARTICLE ||
                              "https://intercom.help/breathehr/en/articles/4829187-managing-two-factor-authentication-2fa"
                            }
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:text-blue-800 underline"
                          >
                            More help
                          </a>
                        </span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </TwoColumnLayout>
    </>
  );
};

